import { Check, Loader2, Star } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { lenderService } from '@/services/lender.service';
import type { Lender } from '@/types/lender';
import type { ListingLender } from '@/types/listing';

interface LenderSelectorProps {
  value: ListingLender[];
  onChange: (lenders: ListingLender[]) => void;
  label?: string;
  maxLenders?: number;
  className?: string;
}

export function LenderSelector({
  value = [],
  onChange,
  label = 'Lenders',
  maxLenders = 10,
  className = '',
}: LenderSelectorProps) {
  const [availableLenders, setAvailableLenders] = useState<Lender[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLenders = useCallback(async () => {
    try {
      setIsLoading(true);
      const lenders = await lenderService.getActive();
      setAvailableLenders(lenders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lenders');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  const isSelected = useCallback(
    (lenderId: string) => value.some((v) => v.lenderId === lenderId),
    [value]
  );

  const handleToggleLender = useCallback(
    (lender: Lender) => {
      const alreadySelected = isSelected(lender._id);

      if (alreadySelected) {
        // Remove
        onChange(value.filter((v) => v.lenderId !== lender._id));
      } else {
        // Add (if under limit)
        if (value.length >= maxLenders) return;
        onChange([
          ...value,
          {
            lenderId: lender._id,
            lender: {
              _id: lender._id,
              name: lender.name,
              companyName: lender.companyName,
              profileLogo: lender.profileLogo,
              rating: lender.rating,
            },
          },
        ]);
      }
    },
    [value, onChange, maxLenders, isSelected]
  );

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-[2px]">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={12}
            className={star <= rating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-[#e5e5e5]'}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-[24px] ${className}`}>
        <Loader2 size={24} className="animate-spin text-[#055135]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-[8px] bg-[#fee] p-[16px] text-[14px] text-[#d4183d] ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-[12px]">
        <span className="text-[14px] font-medium text-[#181818]">
          {label}
          <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
            ({value.length}/{maxLenders} selected)
          </span>
        </span>
        <p className="mt-[4px] text-[12px] text-[#8d8c8f]">
          Click to select lenders for this listing
        </p>
      </div>

      {availableLenders.length === 0 ? (
        <div className="rounded-[8px] border-2 border-dashed border-[#e5e5e5] p-[24px] text-center">
          <p className="text-[14px] text-[#8d8c8f]">No active lenders available</p>
          <p className="mt-[4px] text-[12px] text-[#8d8c8f]">
            Add lenders from the Lenders page first
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-[12px]">
          {availableLenders.map((lender) => {
            const selected = isSelected(lender._id);
            const disabled = !selected && value.length >= maxLenders;

            return (
              <button
                key={lender._id}
                type="button"
                onClick={() => handleToggleLender(lender)}
                disabled={disabled}
                className={`relative rounded-[12px] border-2 p-[16px] text-left transition-all ${
                  selected
                    ? 'border-[#055135] bg-[#f0fdf4]'
                    : disabled
                      ? 'cursor-not-allowed border-[#e5e5e5] bg-[#fafafa] opacity-50'
                      : 'border-[#e5e5e5] bg-white hover:border-[#055135]/50'
                }`}
              >
                {selected && (
                  <div className="absolute -right-[6px] -top-[6px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#055135] text-white">
                    <Check size={14} />
                  </div>
                )}

                <div className="flex items-start gap-[12px]">
                  {lender.profileLogo ? (
                    <img
                      src={lender.profileLogo}
                      alt={lender.name}
                      className="h-[40px] w-[40px] rounded-full border border-[#e5e5e5] object-cover"
                    />
                  ) : (
                    <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#055135] text-[14px] font-semibold text-white">
                      {lender.name.charAt(0)}
                    </div>
                  )}

                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-[14px] font-semibold text-[#181818]">
                      {lender.name}
                    </p>
                    <p className="truncate text-[12px] text-[#8d8c8f]">{lender.companyName}</p>
                    <div className="mt-[4px]">{renderStars(lender.rating)}</div>
                    {lender.loanTypes && lender.loanTypes.length > 0 && (
                      <div className="mt-[8px] flex flex-wrap gap-[4px]">
                        {lender.loanTypes.slice(0, 2).map((type) => (
                          <span
                            key={type}
                            className="rounded-[4px] bg-[#e5e5e5] px-[6px] py-[2px] text-[10px] text-[#646464]"
                          >
                            {type}
                          </span>
                        ))}
                        {lender.loanTypes.length > 2 && (
                          <span className="text-[10px] text-[#8d8c8f]">
                            +{lender.loanTypes.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
